import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Plus } from 'lucide-react'

import { createScriptDocument } from '@/api/studio-production'
import { formatResolvedApiError, resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { ProtoBtn, ProtoInput, ProtoLabel, ProtoModal } from '@/features/studio/components/ui-kit'

export function ScriptEpisodeModal({
  open,
  onClose,
  productionId,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  productionId: string
  onCreated: (scriptId: number) => void
}) {
  const { t } = useTranslation()
  const [title, setTitle] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setTitle('')
    setError(null)
  }, [open])

  async function handleCreate() {
    setBusy(true)
    setError(null)
    try {
      const row = await createScriptDocument(productionId, {
        title: title.trim() || undefined,
      })
      onCreated(row.id)
      onClose()
    } catch (e) {
      setError(formatResolvedApiError(t, resolveApiErrorMessage(e, 'script.addScriptFailed')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <ProtoModal
      open={open}
      onClose={() => {
        if (busy) return
        onClose()
      }}
      title={t('script.addScriptTitle')}
    >
      <p className="text-sm text-zinc-400">{t('script.addScriptHint')}</p>
      <div className="mt-4">
        <ProtoLabel>{t('script.fieldScriptTitle')}</ProtoLabel>
        <ProtoInput
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('script.fieldScriptTitlePlaceholder')}
          maxLength={255}
        />
      </div>
      {error ? <p className="mt-3 text-sm text-rose-300">{error}</p> : null}
      <div className="mt-5 flex justify-end gap-2">
        <ProtoBtn size="sm" variant="secondary" disabled={busy} onClick={onClose}>
          {t('common.cancel')}
        </ProtoBtn>
        <ProtoBtn size="sm" disabled={busy} onClick={() => void handleCreate()}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          {t('script.addScriptConfirm')}
        </ProtoBtn>
      </div>
    </ProtoModal>
  )
}
